import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as planUtils from '../utils/plans';
import * as authUtils from '../utils/auth';
import { BACKEND_URL } from '../utils/backendUrl';
import Footer from './Footer';
import ConfirmationModal from './ConfirmationModal';
import ModernDashboard from './dashboard/ModernDashboard';
import SettingsPage from '../pages/SettingsPage';
import { formatCurrency } from '../utils/currency';
import ModalCloseButton from './ui/ModalCloseButton';

const Dashboard = ({ currentView = 'dashboard', searchQuery = '', onStartNewPlan, onLoadPlan, onShowPrivacy, onShowTerms, onNavigate }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const [plans, setPlans] = useState([]);
  const [sharedPlans, setSharedPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, planId: null });
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [deleteAccountConfirm, setDeleteAccountConfirm] = useState(false);

  useEffect(() => {
    // Only load plans if authenticated
    // Add a small delay to ensure token is available after login
    if (isAuthenticated) {
      const timer = setTimeout(() => {
        loadPlans();
      }, 200);
      return () => clearTimeout(timer);
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPlans = async () => {
    // Don't load if not authenticated
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const allPlans = await planUtils.getPlans();
      // Separate owned and shared plans
      const owned = allPlans.filter(p => p.is_owner !== false);
      const shared = allPlans.filter(p => p.is_owner === false);
      setPlans(owned);
      setSharedPlans(shared);
    } catch (error) {
      // Only log if it's not an auth error (which is expected for unauthenticated users)
      if (error.message && !error.message.includes('not authenticated') && !error.message.includes('Authentication failed')) {
        console.error('Failed to load plans:', error);
      }
      // Set empty arrays on error
      setPlans([]);
      setSharedPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPlanBudget = (plan) => {
    const budgetVal = plan?.event_data?.budget;
    if (budgetVal === null || budgetVal === undefined || budgetVal === '' || Number(budgetVal) <= 0) {
      return 'Budget not set';
    }
    return formatCurrency(budgetVal, plan?.event_data?.country || 'US');
  };

  const formatPlanGuests = (plan) => {
    const guests = plan?.event_data?.guest_count;
    if (guests === null || guests === undefined || guests === '' || Number(guests) <= 0) {
      return 'Guest count not set';
    }
    return `${guests} guest${Number(guests) === 1 ? '' : 's'}`;
  };

  const handleLoadPlan = async (plan) => {
    try {
      const fullPlan = await planUtils.getPlan(plan.plan_id);
      onLoadPlan(fullPlan);
    } catch (error) {
      console.error('Failed to load plan:', error);
      alert('Failed to load plan. Please try again.');
    }
  };

  const handleDeletePlan = async (planId, e) => {
    e.stopPropagation();
    setDeleteConfirm({ isOpen: true, planId });
  };

  const confirmDeletePlan = async () => {
    if (!deleteConfirm.planId) return;

    try {
      await planUtils.deletePlan(deleteConfirm.planId);
      await loadPlans();
      if (selectedPlan?.plan_id === deleteConfirm.planId) {
        setSelectedPlan(null);
      }
      setDeleteConfirm({ isOpen: false, planId: null });
    } catch (error) {
      console.error('Failed to delete plan:', error);
      alert('Failed to delete plan. Please try again.');
      setDeleteConfirm({ isOpen: false, planId: null });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    // Get dates at start of day in local timezone for accurate day comparison
    const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Calculate difference in calendar days
    const diffTime = nowStart - dateStart;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  const getEventTypeIcon = (eventType) => {
    const icons = {
      'Birthday': '🎂',
      'Wedding': '💒',
      'Baby Shower': '👶',
      'Graduation': '🎓',
      'Anniversary': '💍',
      'Corporate': '🏢',
      'Holiday': '🎄',
      'Other': '🎉'
    };
    return icons[eventType] || '🎉';
  };

  // Render different views based on currentView prop
  if (currentView === 'settings') {
    return (
      <SettingsPage
        onBack={() => {
          // This will be handled by parent navigation
        }}
      />
    );
  }

  // Use ModernDashboard for plans views
  if (currentView === 'dashboard' || currentView === 'plans' || currentView === 'shared') {
    return (
      <ModernDashboard
        currentView={currentView}
        searchQuery={searchQuery}
        onStartNewPlan={onStartNewPlan}
        onLoadPlan={onLoadPlan}
        onShowPrivacy={onShowPrivacy}
        onShowTerms={onShowTerms}
        onNavigate={onNavigate}
      />
    );
  }

  // Legacy dashboard view (fallback)
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Welcome Section */}
        <div className="mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}! 👋
          </h1>
          <p className="text-gray-600 text-lg">Continue planning or start a new event</p>
        </div>

        {/* Action Button */}
        <div className="mb-8 sm:mb-12">
          <button
            onClick={onStartNewPlan}
            className="w-full sm:w-auto px-8 py-4 bg-zinc-900 text-white font-bold text-lg rounded-xl shadow hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2"
          >
            <span className="text-2xl">✨</span>
            <span>Create New Plan</span>
          </button>
        </div>

        {/* Plans Section */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Your Plans</h2>
            {plans.length > 0 && (
              <span className="text-sm text-gray-500">{plans.length} {plans.length === 1 ? 'plan' : 'plans'}</span>
            )}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900 mb-4"></div>
              <p className="text-gray-500">Loading your plans...</p>
            </div>
          ) : plans.length === 0 ? (
            <div className="bg-white rounded-2xl border-2 border-dashed border-gray-300 p-12 text-center">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No plans yet</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Start planning your first event! Click "Create New Plan" to get started.
              </p>
              <button
                onClick={onStartNewPlan}
                className="px-6 py-3 bg-zinc-900 text-white font-semibold rounded-lg hover:bg-zinc-800 transition"
              >
                Create Your First Plan
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {plans.map((plan) => {
                const eventType = plan.event_data?.event_type || 'Event type not set';
                const hasThemes = plan.themes && plan.themes.length > 0;
                const selectedTheme = plan.selected_theme;
                const themeCount = plan.themes?.length || 0;

                return (
                  <div
                    key={plan.plan_id}
                    className="bg-white rounded-xl border-2 border-gray-200 hover:border-stone-400 hover:shadow-lg transition-all duration-200 overflow-hidden group cursor-pointer"
                    onClick={() => handleLoadPlan(plan)}
                  >
                    {/* Card Header */}
                    <div className="p-5 pb-4 border-b border-gray-100">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="text-3xl">{getEventTypeIcon(eventType)}</div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-gray-900 whitespace-normal break-words text-lg">
                              {eventType}
                            </h3>
                            <p className="text-sm text-gray-500 mt-0.5">
                              {formatDate(plan.updated_at)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleDeletePlan(plan.plan_id, e)}
                          className="opacity-0 group-hover:opacity-100 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Delete plan"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-5 pt-4">
                      <div className="space-y-3">
                        {/* Event Details */}
                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <span>💰</span>
                            <span className="font-medium">{formatPlanBudget(plan)}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <span>👥</span>
                            <span className="font-medium">{formatPlanGuests(plan)}</span>
                          </div>
                        </div>

                        {/* Progress Indicator */}
                        <div className="pt-3 border-t border-gray-100">
                          {hasThemes ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600 font-medium">Progress</span>
                                <span className="text-zinc-900 font-semibold">
                                  {selectedTheme ? 'Theme Selected' : `${themeCount} Themes`}
                                </span>
                              </div>
                              {selectedTheme && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <span className="text-green-500">✓</span>
                                  <span>Ready to shop</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">
                              <span className="text-amber-500">⏳</span> In progress
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Card Footer - Hover Action */}
                    <div className="px-5 pb-5 pt-0">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                          <span>Continue Planning</span>
                          <span>→</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Shared Plans Section */}
          {sharedPlans.length > 0 && (
            <div className="mt-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Shared With Me</h2>
                <span className="text-sm text-gray-500">{sharedPlans.length} {sharedPlans.length === 1 ? 'plan' : 'plans'}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {sharedPlans.map((plan) => {
                  const eventType = plan.event_data?.event_type || 'Event type not set';
                  return (
                    <div
                      key={plan.plan_id}
                      className="bg-white rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200 overflow-hidden group cursor-pointer"
                      onClick={() => handleLoadPlan(plan)}
                    >
                      <div className="p-5 pb-4 border-b border-gray-100">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="text-3xl">{getEventTypeIcon(eventType)}</div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-gray-900 whitespace-normal break-words text-lg">
                              {eventType}
                            </h3>
                            <p className="text-sm text-gray-500 mt-0.5">
                              Shared • {formatDate(plan.updated_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="p-5 pt-4">
                        <div className="flex items-center gap-4 text-sm mb-3">
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <span>💰</span>
                            <span className="font-medium">{formatPlanBudget(plan)}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <span>👥</span>
                            <span className="font-medium">{formatPlanGuests(plan)}</span>
                          </div>
                        </div>
                        <div className="pt-3 border-t border-gray-100">
                          <div className="text-xs text-gray-500">
                            {plan.permissions?.can_edit_shopping_list || plan.permissions?.can_edit_theme ? 'Can edit' : 'View only'}
                          </div>
                        </div>
                      </div>
                      <div className="px-5 pb-5 pt-0">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="text-sm font-semibold text-blue-600 flex items-center gap-2">
                            <span>View Plan</span>
                            <span>→</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Account Settings Modal */}
      {showAccountSettings && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Account Settings</h2>
              <ModalCloseButton onClick={() => setShowAccountSettings(false)} className="h-9 px-3 text-xs" />
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {user?.email}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                  {user?.name || 'Not set'}
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={() => setDeleteAccountConfirm(true)}
                  className="w-full px-4 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation */}
      <ConfirmationModal
        isOpen={deleteAccountConfirm}
        onClose={() => setDeleteAccountConfirm(false)}
        onConfirm={async () => {
          try {
            const response = await fetch(`${BACKEND_URL}/api/auth/account`, {
              method: 'DELETE',
              headers: {
                ...authUtils.getAuthHeader(),
              },
            });
            if (!response.ok) throw new Error('Failed to delete account');
            logout();
            window.location.href = '/';
          } catch (error) {
            console.error('Failed to delete account:', error);
            alert('Failed to delete account. Please try again.');
          }
        }}
        title="Delete Account?"
        message="This will permanently delete your account and all your plans. This action cannot be undone. Are you absolutely sure?"
        confirmText="Delete Account"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Footer */}
      <Footer onShowPrivacy={onShowPrivacy} onShowTerms={onShowTerms} />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, planId: null })}
        onConfirm={confirmDeletePlan}
        title="Delete Plan?"
        message={
          user?.email && ["sandheepr89@gmail.com", "sandheep.r.krishnan@gmail.com"].includes(user.email)
            ? "Are you sure you want to delete this plan? This action cannot be undone."
            : "Are you sure you want to delete this plan? This will permanently remove your event data. NOTE: As a free user, deleting this plan means you will lose your one-time free opportunity to plan an event."
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

    </div>
  );
};

export default Dashboard;
